import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';
import {
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const CAMPAIGN_PAGE_SIZE = 10;

function NewsletterSubscribersReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaignPage, setCampaignPage] = useState(1);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [newsletterByCampaign, setNewsletterByCampaign] = useState({
    items: [],
    page: 1,
    limit: CAMPAIGN_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  const fetchData = useCallback(async (page) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        includeNewsletterCampaigns: 'true',
        campaignPage: String(page),
        campaignLimit: String(CAMPAIGN_PAGE_SIZE),
      });
      const response = await api.get(`/admin/reports?${params.toString()}`);
      const { campaigns } = response.data;
      setTotalSubscribers(campaigns?.totalNewsletterSubscribers ?? 0);
      setNewsletterByCampaign(
        campaigns?.newsletterByCampaign ?? {
          items: [],
          page: 1,
          limit: CAMPAIGN_PAGE_SIZE,
          total: 0,
          totalPages: 1,
        }
      );
      setError('');
    } catch (err) {
      console.error('Fetch newsletter report error:', err);
      setError('Failed to load newsletter data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(campaignPage);
  }, [campaignPage, fetchData]);

  const { items: campaignItems, total: campaignTotal, totalPages: campaignTotalPages } =
    newsletterByCampaign;
  const listPage = campaignPage;
  const showingFrom = campaignTotal === 0 ? 0 : (listPage - 1) * CAMPAIGN_PAGE_SIZE + 1;
  const showingTo = Math.min(listPage * CAMPAIGN_PAGE_SIZE, campaignTotal);

  const isCampaignActive = (campaign) =>
    campaign.status === 'active' &&
    new Date(campaign.start_date) <= new Date() &&
    new Date(campaign.end_date) >= new Date();

  if (error) {
    return (
      <AdminLayout activeSection="REPORTING_NEWSLETTER">
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="REPORTING_NEWSLETTER">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/reports')}
          className="text-white hover:text-gray-200 mr-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">NEWSLETTER</h1>
          <p className="text-white/70">
            {totalSubscribers.toLocaleString()} subscriber{totalSubscribers !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 text-sm text-gray-500">
          {loading && campaignItems.length === 0
            ? 'Loading...'
            : campaignTotal === 0
              ? 'No campaigns'
              : `${showingFrom}–${showingTo} of ${campaignTotal}`}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  Entries
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  Subscribers
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && campaignItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500 text-sm">
                    Loading...
                  </td>
                </tr>
              ) : (
                campaignItems.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          isCampaignActive(campaign)
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isCampaignActive(campaign) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {campaign.entries_count ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-teal-700 text-center">
                      {campaign.newsletter_count ?? 0}
                    </td>
                  </tr>
                ))
              )}
              {!loading && campaignItems.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500 text-sm">
                    No campaigns found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 flex items-center justify-end border-t border-gray-200">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCampaignPage((p) => Math.max(1, p - 1))}
              disabled={listPage <= 1 || loading}
              className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">
              {listPage} / {campaignTotalPages}
            </span>
            <button
              type="button"
              onClick={() => setCampaignPage((p) => Math.min(campaignTotalPages, p + 1))}
              disabled={listPage >= campaignTotalPages || loading}
              className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default NewsletterSubscribersReport;
